import React, { useState } from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import DatasetDialog, { primaryClass, neutralClass, labelClass } from 'features/datasets/components/DatasetDialog';
import { buildImportMapping, cleanCol } from 'features/datasets/utils/importParsers';

/**
 * Map each column of the chosen file onto the dataset: one of its columns, the
 * row's expected output, metadata or tags, a new column, or nothing. The first
 * rows are shown so the mapping can be checked against real values.
 */
export default function ImportDialog({ table, columns, onApply, onClose }) {
  const { header, dataRows, warnings = [], filename } = table;
  const [mapping, setMapping] = useState(() => buildImportMapping(header, columns));
  const used = mapping.filter((m) => m !== '__skip__').length;
  const n = dataRows.length;

  return (
    <DatasetDialog title="Import rows" onClose={onClose} width="max-w-[560px]"
      footer={(
        <>
          <button type="button" onClick={onClose} className={neutralClass}>Cancel</button>
          <button type="button" disabled={!used} onClick={() => onApply(mapping)} className={primaryClass}>
            Add {n.toLocaleString()} {n === 1 ? 'row' : 'rows'}
          </button>
        </>
      )}>
      <p className="text-[12px] text-[color:var(--text-muted)]">
        <span className="font-medium text-[color:var(--text-primary)]">{filename}</span>
        {` · ${n.toLocaleString()} ${n === 1 ? 'row' : 'rows'}`}
      </p>
      {warnings.map((w) => <p key={w} className="text-[12px] text-[color:var(--feedback-warning)]">{w}</p>)}

      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-[1fr_1fr] gap-x-3">
          <span className={labelClass}>File column</span>
          <span className={labelClass}>Goes to</span>
        </div>
        {header.map((h, i) => (
          <div key={`${h}-${i}`} className="grid grid-cols-[1fr_1fr] items-center gap-x-3">
            <span className="truncate font-mono text-[11.5px] text-[color:var(--text-primary)]" title={h}>{h}</span>
            <BrandDropdown
              value={mapping[i]} compact triggerClassName="w-full" ariaLabel={`Map ${h}`}
              onChange={(val) => setMapping((m) => m.map((x, j) => (j === i ? val : x)))}
              options={[
                ...columns.map((c) => ({ value: c, label: c })),
                { value: '__expected', label: 'Expected' },
                { value: '__metadata', label: 'Metadata' },
                { value: '__tags', label: 'Tags' },
                { value: '__new__', label: `New column “${cleanCol(h) || 'column'}”` },
                { value: '__skip__', label: 'Skip' },
              ]}
            />
          </div>
        ))}
      </div>

      <div className="max-h-32 overflow-auto rounded-md border border-[color:var(--border-default)]">
        <table className="w-full text-left text-[11px]">
          <thead>
            <tr className="border-b border-[color:var(--border-default)] text-[color:var(--text-muted)]">
              {header.map((h, i) => <th key={i} className="px-2 py-1 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {dataRows.slice(0, 3).map((r, ri) => (
              <tr key={ri} className="border-b border-[color:var(--border-subtle)] last:border-b-0">
                {header.map((_, ci) => <td key={ci} className="max-w-[160px] truncate px-2 py-1 text-[color:var(--text-secondary)]">{String(r[ci] ?? '')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DatasetDialog>
  );
}
